import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { IMAGES } from "@/data/content";

const LINES = ["Connecting Businesses.", "Moving Possibilities."];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex items-center bg-[#0B192C] text-white overflow-hidden"
    >
      <motion.div style={{ y: imgY }} className="absolute inset-0 -bottom-24">
        <img
          src={IMAGES.hero}
          alt="Aerial view of a container vessel at sea"
          className="w-full h-full object-cover opacity-45"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B192C] via-[#0B192C]/80 to-[#0B192C]/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B192C] via-transparent to-[#0B192C]/40" />
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-40 pb-28 w-full"
      >
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="flex items-center gap-4 mb-8"
        >
          <span className="h-px w-12 bg-[#C5A059]" />
          <p data-testid="hero-overline" className="text-xs sm:text-sm uppercase tracking-[0.38em] font-semibold text-[#C5A059]">
            Trivena Exim LLP
          </p>
        </motion.div>

        <h1
          data-testid="hero-heading"
          className="font-serif text-4xl sm:text-5xl lg:text-[4.4rem] tracking-tight leading-[1.08] max-w-3xl"
        >
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay: 0.35 + i * 0.18, ease: [0.22, 1, 0.36, 1] }}
              >
                {i === 1 ? (
                  <>
                    Moving <em className="not-italic text-[#C5A059]">Possibilities.</em>
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          data-testid="hero-subtext"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-7 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed"
        >
          Reliable import, export, logistics and trade solutions for businesses across diverse
          industries.
        </motion.p>

      </motion.div>

      <motion.div
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 1.4, delay: 1.3, ease: [0.22, 1, 0.36, 1] }}
        className="absolute bottom-0 inset-x-0 h-px origin-left bg-gradient-to-r from-[#C5A059] via-[#C5A059]/40 to-transparent"
      />
    </section>
  );
}
